import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { toastSlipUploaded } from "@/components/AnimatedToasts";
import ThanksOverlay from "@/components/ThanksOverlay";
import { UploadSimple, Image as ImageIcon, MagicWand, Trash, Plus, WhatsappLogo, CheckCircle, XCircle, CircleNotch } from "@phosphor-icons/react";
import { sanitizeUtr, isValidUtr, UTR_UPLOAD_FORMAT_ERROR } from "@/lib/utr";

const SHARE_CACHE = "micm-share-v1";
const UPLOAD_CONCURRENCY = 3;
const OCR_CONCURRENCY = 2;   // Gemini rate-limits us if we fire >~3 in parallel
const OCR_RETRIES = 2;
const OCR_TIMEOUT_MS = 20000; // v1.22.0 — cap per-slip OCR wait so bulk uploads never freeze

// Simple concurrency pool — process items with at most `limit` in flight.
async function runWithConcurrency(items, limit, worker) {
    const executing = [];
    const results = [];
    for (const item of items) {
        const p = Promise.resolve().then(() => worker(item));
        results.push(p);
        const e = p.then(() => executing.splice(executing.indexOf(e), 1));
        executing.push(e);
        if (executing.length >= limit) await Promise.race(executing);
    }
    return Promise.all(results);
}

const makeItem = (file) => ({
    id: crypto.randomUUID(),
    file,
    preview: file.type?.startsWith("image/") ? URL.createObjectURL(file) : null,
    ocring: file.type?.startsWith("image/"),
    amount: "",
    utr: "",
    payment_date: new Date().toISOString().slice(0, 10),
    payment_app: "PhonePe",
    receiver_upi_id: "",
    notes: "",
    uploadStatus: null, // null | "queued" | "uploading" | "done" | "error"
    uploadError: null,
});

export default function CustomerUpload() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [items, setItems] = useState([]);
    const [busy, setBusy] = useState(false);
    const [showThanks, setShowThanks] = useState(false);
    const [thanksCount, setThanksCount] = useState(0);
    const pickedShared = useRef(false);

    const runOCR = useCallback(async (itemId, file) => {
        // Bulk uploads hit Gemini's rate limits when > ~3 OCR calls run in
        // parallel. Retry up to OCR_RETRIES times on failure so a transient
        // 429/timeout doesn't force the user to type amount+date manually.
        let attempt = 0;
        while (attempt <= OCR_RETRIES) {
            try {
                const fd = new FormData();
                fd.append("file", file);
                const { data } = await api.post("/uploads/ocr", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                    timeout: OCR_TIMEOUT_MS,
                });
                setItems((prev) => prev.map((s) => s.id === itemId ? {
                    ...s,
                    ocring: false,
                    amount: data.amount != null ? String(data.amount) : s.amount,
                    utr: data.utr || s.utr,
                    payment_date: data.payment_date || s.payment_date,
                    payment_app: data.payment_app || s.payment_app,
                    receiver_upi_id: data.receiver_upi_id || s.receiver_upi_id,
                } : s));
                if (data.note && /limit|unavailable/i.test(data.note)) toast.warning(data.note, { duration: 9000, id: "ocr-limit" });
                return;
            } catch (_e) {
                attempt += 1;
                if (attempt > OCR_RETRIES) {
                    setItems((prev) => prev.map((s) => s.id === itemId ? { ...s, ocring: false } : s));
                    return;
                }
                // Exponential backoff — 600ms, 1200ms
                await new Promise((r) => setTimeout(r, 600 * attempt));
            }
        }
    }, []);

    const addFiles = useCallback((fileList) => {
        const files = Array.from(fileList || []);
        if (files.length === 0) return;
        const newItems = files.map(makeItem);
        setItems((prev) => [...prev, ...newItems]);
        // Cap OCR concurrency to avoid rate-limit-driven silent failures.
        const imageItems = newItems.filter((it) => it.file.type?.startsWith("image/"));
        runWithConcurrency(imageItems, OCR_CONCURRENCY, (it) => runOCR(it.id, it.file));
    }, [runOCR]);

    // Pickup shared files from PWA share target (WhatsApp -> app)
    useEffect(() => {
        if (pickedShared.current) return;
        if (searchParams.get("shared") !== "1") return;
        pickedShared.current = true;
        (async () => {
            if (!("caches" in window)) {
                toast.warning("Your browser doesn't support offline share — please pick files manually.");
                return;
            }
            try {
                const cache = await caches.open(SHARE_CACHE);
                const manifestResp = await cache.match("/shared/_manifest.json");
                if (!manifestResp) {
                    toast.info("No shared slips found — pick files below or share again from WhatsApp.");
                    return;
                }
                const stored = await manifestResp.json();
                const loaded = [];
                for (const entry of stored) {
                    const resp = await cache.match(entry.key);
                    if (!resp) continue;
                    const blob = await resp.blob();
                    loaded.push(new File([blob], entry.name || "slip.jpg", { type: entry.type || blob.type || "image/jpeg" }));
                    await cache.delete(entry.key);
                }
                await cache.delete("/shared/_manifest.json");
                if (loaded.length > 0) {
                    addFiles(loaded);
                    toast.success(`${loaded.length} slip${loaded.length > 1 ? "s" : ""} received from WhatsApp — reading details…`);
                } else {
                    toast.warning("Couldn't read the shared files — please pick them manually.");
                }
            } catch (_e) {
                toast.error("Failed to load shared slips — please pick files manually.");
            } finally {
                // clean URL
                const p = new URLSearchParams(searchParams);
                p.delete("shared");
                setSearchParams(p, { replace: true });
            }
        })();
    }, [searchParams, setSearchParams, addFiles]);

    const updateItem = (id, patch) => setItems((prev) => prev.map((s) => s.id === id ? { ...s, ...patch } : s));
    const removeItem = (id) => setItems((prev) => prev.filter((s) => s.id !== id));

    const submit = async (e) => {
        e.preventDefault();
        if (items.length === 0) return toast.error("Add at least one slip");
    // v1.22.0 — Once at least one slip has amount+date, allow submit even if
    // OCR is still reading the rest. Also fires a redirect on ANY success so
    // the page never gets stuck open when a few slips fail.
    // v1.25.51 — UTR is now mandatory (matches the backend gate at /uploads).
    // A missing UTR bypasses the unique index and lets duplicates sneak in,
    // so we block at the client too and highlight the row in red.
    const missingReason = (s) => {
        const need = [];
        if (!s.amount) need.push("Amount");
        if (!s.payment_date) need.push("Date");
        const u = (s.utr || "").trim();
        if (!u) need.push("UTR");
        else if (!isValidUtr(u)) need.push("valid 12-digit UTR");
        return need;
    };
    const invalidItems = items.filter((s) => missingReason(s).length > 0);
    if (invalidItems.length > 0) {
            setItems((prev) => prev.map((s) => {
                const need = missingReason(s);
                return need.length > 0
                    ? { ...s, uploadStatus: "error", uploadError: `${need.join(" & ")} missing` }
                    : s;
            }));
            toast.error(`${invalidItems.length} slip${invalidItems.length > 1 ? "s" : ""} missing UTR / Amount / Date — highlighted in red below. UTR is mandatory.`);
            // Scroll to first invalid
            setTimeout(() => {
                const first = document.querySelector('[data-status="error"]');
                if (first) first.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 100);
            return;
        }

        setBusy(true);
        // Mark all as queued so their status pill shows immediately
        setItems((prev) => prev.map((s) => ({ ...s, uploadStatus: "queued", uploadError: null })));

        let ok = 0, fail = 0;
        const errors = [];

    const uploadOne = async (s) => {
        setItems((prev) => prev.map((x) => x.id === s.id ? { ...x, uploadStatus: "uploading", uploadError: null } : x));
        const fd = new FormData();
        fd.append("file", s.file);
        fd.append("amount", s.amount);
        fd.append("utr", s.utr);
        fd.append("payment_date", s.payment_date);
        fd.append("payment_app", s.payment_app);
        fd.append("receiver_upi_id", s.receiver_upi_id || "");
        fd.append("notes", s.notes || "");
        try {
            await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
            ok += 1;
            setItems((prev) => prev.map((x) => x.id === s.id ? { ...x, uploadStatus: "done" } : x));
            return { ok: true };
        } catch (err) {
            fail += 1;
            const msg = formatApiError(err);
            setItems((prev) => prev.map((x) => x.id === s.id ? { ...x, uploadStatus: "error", uploadError: msg } : x));
            return { ok: false, msg };
        }
    };

    // v1.27.53 — server-side party+date+amount+mode duplicate guard removed.
    // UTR is the sole uniqueness key. On the very rare 409 that still comes
    // back (a re-used UTR), just show the error toast — no "Post anyway"
    // action, since re-using a real UTR is always wrong.
    await runWithConcurrency(items, UPLOAD_CONCURRENCY, async (s) => {
        const res = await uploadOne(s);
        if (!res.ok) errors.push(`${s.file.name}: ${res.msg}`);
    });

        setBusy(false);
        if (ok > 0) {
            toastSlipUploaded(ok, fail ? `${fail} failed — retry below.` : "");
            setThanksCount(ok);
            setShowThanks(true);
        }
        if (fail > 0) {
            errors.slice(0, 3).forEach((msg) => toast.error(msg));
        }
        // v1.25.45 — Redirect AFTER the Thanks overlay auto-closes (~3.4s)
        // so the party actually sees the celebration before landing on
        // "My uploads". If everything failed, stay on the page so they retry.
        if (ok > 0) {
            setTimeout(() => navigate("/portal/my-uploads"), 3800);
        }
    };

    // Live counters — used for progress banner & submit button
    const ocrRemaining = items.filter((s) => s.ocring).length;
    const uploadedCount = items.filter((s) => s.uploadStatus === "done").length;
    const failedCount = items.filter((s) => s.uploadStatus === "error").length;
    const processedCount = uploadedCount + failedCount;
    const progressPct = items.length ? Math.round((processedCount / items.length) * 100) : 0;

    const isIOS = React.useMemo(() =>
        typeof navigator !== "undefined" && /iP(hone|ad|od)/.test(navigator.userAgent), []);

    return (
        <div className="space-y-6" data-testid="customer-upload-page">
            <ThanksOverlay open={showThanks} count={thanksCount} onClose={() => setShowThanks(false)} />
            <div>
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Payment</div>
                <h1 className="mt-1 font-display text-4xl font-bold tracking-tight text-slate-900">Upload Payment Slips</h1>
                <p className="mt-2 text-sm text-slate-500">
                    Attach one or more PhonePe / Google Pay / Paytm screenshots — details are read automatically.
                </p>
                {isIOS ? (
                    <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="ios-tip-banner">
                        <div className="font-semibold text-amber-900">iPhone / iPad users — WhatsApp share isn't supported on iOS.</div>
                        <div className="mt-1">Long-press the payment screenshot in WhatsApp → <span className="font-semibold">Save Image</span>, then come back here and tap <span className="font-semibold">"Upload from Photos"</span> below (you can select multiple).</div>
                    </div>
                ) : (
                    <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs text-emerald-800">
                        <WhatsappLogo size={14} weight="fill" /> Tip: from WhatsApp, tap Share → choose <span className="font-semibold">MD Impex</span> to bring slips straight here.
                    </div>
                )}
            </div>

            {/* File picker card */}
            <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                <label
                    htmlFor="slip-file"
                    data-testid="slip-file-drop"
                    className="flex h-40 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 text-center transition-colors hover:border-blue-400 hover:bg-blue-50/40"
                >
                    <ImageIcon size={32} className="text-slate-400" weight="duotone" />
                    <div className="mt-3 text-sm font-semibold text-slate-800">
                        {isIOS ? "Upload from Photos" : "Tap to select payment screenshots"}
                    </div>
                    <div className="mt-1 text-xs text-slate-500">You can select multiple · PNG · JPG · PDF</div>
                </label>
                <input
                    id="slip-file"
                    data-testid="slip-file-input"
                    type="file"
                    accept="image/*,.pdf"
                    multiple
                    className="hidden"
                    onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
                />
            </div>

            {/* Slip list */}
            {items.length > 0 && (
                <form onSubmit={submit} className="space-y-4" data-testid="customer-slips-list">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="text-sm text-slate-600" data-testid="customer-upload-count">
                            <span className="font-semibold text-slate-900">{items.length}</span> slip{items.length === 1 ? "" : "s"} ready
                            {ocrRemaining > 0 && !busy && (
                                <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700" data-testid="ocr-remaining-badge">
                                    <MagicWand size={11} weight="fill" className="animate-pulse" />
                                    Reading {ocrRemaining} slip{ocrRemaining === 1 ? "" : "s"}…
                                </span>
                            )}
                        </div>
                        <label
                            htmlFor="slip-file-add"
                            className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-100"
                        >
                            <Plus size={12} /> Add more
                        </label>
                        <input
                            id="slip-file-add"
                            type="file"
                            accept="image/*,.pdf"
                            multiple
                            className="hidden"
                            onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
                        />
                    </div>

                    <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900" data-testid="ocr-locked-banner">
                        <span className="font-semibold">Details are auto-extracted from your slip and cannot be edited.</span> Just check the image is clear and submit — our team verifies every slip.
                    </div>
                    <div className="space-y-3">
                        {items.map((s, idx) => (
                            <SlipCard
                                key={s.id}
                                idx={idx}
                                item={s}
                                onChange={(patch) => updateItem(s.id, patch)}
                                onRemove={() => removeItem(s.id)}
                            />
                        ))}
                    </div>

                    <div className="sticky bottom-0 -mx-4 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur sm:mx-0 sm:rounded-lg sm:border sm:border-slate-200 sm:px-4 sm:shadow-lg">
                        {busy && (
                            <div className="mb-3" data-testid="upload-progress-panel">
                                <div className="flex items-center justify-between text-xs">
                                    <span className="font-medium text-slate-700">
                                        Uploading {processedCount} of {items.length}
                                        {failedCount > 0 && <span className="ml-1 text-rose-600">· {failedCount} failed</span>}
                                    </span>
                                    <span className="font-mono text-slate-500">{progressPct}%</span>
                                </div>
                                <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-slate-200">
                                    <div
                                        data-testid="upload-progress-bar"
                                        className="h-full bg-emerald-500 transition-all duration-300"
                                        style={{ width: `${progressPct}%` }}
                                    />
                                </div>
                            </div>
                        )}
                        <Button
                            type="submit"
                            disabled={busy || items.length === 0}
                            data-testid="upload-submit-btn"
                            className="h-12 w-full bg-blue-600 text-base text-white hover:bg-blue-700"
                        >
                            <UploadSimple size={18} className="mr-2" />
                            {busy
                                ? `Uploading ${processedCount}/${items.length}…`
                                : ocrRemaining > 0
                                    ? `Submit anyway — ${ocrRemaining} slip${ocrRemaining === 1 ? "" : "s"} still reading`
                                    : `Submit ${items.length} slip${items.length === 1 ? "" : "s"} for Verification`}
                        </Button>
                    </div>
                </form>
            )}
        </div>
    );
}

function SlipCard({ idx, item, onChange, onRemove }) {
    const missingAmount = !item.amount;
    const missingDate = !item.payment_date;
    // Row highlight follows the lifecycle: OCR / queued / uploading → RED (processing),
    // done → GREEN (complete), error → deeper red. User feedback: "till upload not
    // complete or OCR working should show in red color" (iter 51).
    const status = item.uploadStatus;
    const cardClass =
        status === "done"
            ? "border-emerald-400 bg-emerald-50 ring-2 ring-emerald-200"
            : status === "error"
                ? "border-rose-500 bg-rose-100 ring-2 ring-rose-300"
                : status === "uploading"
                    ? "border-rose-400 bg-rose-50 ring-2 ring-rose-200 animate-pulse"
                    : status === "queued"
                        ? "border-rose-300 bg-rose-50/70"
                        : item.ocring
                            ? "border-rose-300 bg-rose-50/60 ring-1 ring-rose-200 animate-pulse"
                            : "border-slate-200 bg-white";
    return (
        <div
            data-testid={`customer-slip-${idx}`}
            data-status={status || (item.ocring ? "ocr" : "ready")}
            className={`rounded-lg border p-3 shadow-sm transition-all duration-300 ${cardClass}`}
        >
            <div className="flex gap-3">
                <div className="w-20 shrink-0 sm:w-24">
                    {item.preview ? (
                        <img src={item.preview} alt="preview" className={`h-20 w-full rounded object-cover sm:h-24 ${item.ocring ? "opacity-70" : ""}`} />
                    ) : (
                        <div className="flex h-20 w-full items-center justify-center rounded bg-slate-100 text-xs text-slate-500 sm:h-24">PDF</div>
                    )}
                    <div className="mt-1 truncate text-[10px] text-slate-500" title={item.file.name}>{item.file.name}</div>
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                        <div className="text-xs font-medium uppercase tracking-[0.15em] text-slate-500">
                            {status === "done" ? (
                                <span className="inline-flex items-center gap-1 text-emerald-700" data-testid={`slip-status-done-${idx}`}>
                                    <CheckCircle size={12} weight="fill" /> Uploaded
                                </span>
                            ) : status === "error" ? (
                                <span className="inline-flex items-center gap-1 text-rose-700" data-testid={`slip-status-error-${idx}`}>
                                    <XCircle size={12} weight="fill" /> Failed
                                </span>
                            ) : status === "uploading" ? (
                                <span className="inline-flex items-center gap-1 text-blue-600" data-testid={`slip-status-uploading-${idx}`}>
                                    <CircleNotch size={12} weight="bold" className="animate-spin" /> Uploading…
                                </span>
                            ) : status === "queued" ? (
                                <span className="inline-flex items-center gap-1 text-slate-500" data-testid={`slip-status-queued-${idx}`}>
                                    <CircleNotch size={12} weight="bold" /> Queued
                                </span>
                            ) : item.ocring ? (
                                <span className="inline-flex items-center gap-1 text-blue-600" data-testid={`slip-status-ocr-${idx}`}>
                                    <MagicWand size={12} weight="fill" className="animate-pulse" /> Reading slip…
                                </span>
                            ) : (
                                `Slip ${idx + 1}`
                            )}
                        </div>
                        <button
                            type="button"
                            onClick={onRemove}
                            disabled={status === "uploading" || status === "done"}
                            data-testid={`customer-slip-remove-${idx}`}
                            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-40"
                            aria-label="Remove slip"
                        >
                            <Trash size={14} />
                        </button>
                    </div>
                    {status === "error" && item.uploadError && (
                        <div className="mt-1 rounded border border-rose-200 bg-rose-100/70 px-2 py-1 text-[11px] text-rose-800" data-testid={`slip-error-msg-${idx}`}>
                            {item.uploadError}
                        </div>
                    )}
                    <div className="mt-2 grid grid-cols-2 gap-2">
                        <div>
                            <Label className="text-[10px] uppercase text-slate-500">Amount (₹) *</Label>
                            <Input
                                data-testid={`customer-slip-amount-${idx}`}
                                type="number" step="0.01"
                                value={item.amount}
                                readOnly
                                disabled
                                title="Auto-extracted from your slip — cannot be edited"
                                className={`mt-0.5 h-9 text-sm tabular-nums cursor-not-allowed bg-slate-100 text-slate-600 ${missingAmount ? "border-rose-300" : ""}`}
                            />
                        </div>
                        <div>
                            <Label className="text-[10px] uppercase text-slate-500">Date *</Label>
                            <Input
                                data-testid={`customer-slip-date-${idx}`}
                                type="date"
                                value={item.payment_date}
                                readOnly
                                disabled
                                title="Auto-extracted from your slip — cannot be edited"
                                className={`mt-0.5 h-9 text-sm cursor-not-allowed bg-slate-100 text-slate-600 ${missingDate ? "border-rose-300" : ""}`}
                            />
                        </div>
                        <div>
                            <Label className="text-[10px] uppercase text-slate-500">UTR / Ref *</Label>
                            <Input
                                data-testid={`customer-slip-utr-${idx}`}
                                value={item.utr}
                                inputMode="numeric"
                                maxLength={12}
                                readOnly
                                disabled
                                title="Auto-extracted from your slip — cannot be edited"
                                className={`mt-0.5 h-9 font-mono text-xs cursor-not-allowed bg-slate-100 text-slate-600 ${item.utr && !isValidUtr(item.utr) ? "border-rose-300" : ""}`}
                            />
                            {item.utr && !isValidUtr(item.utr) ? (
                                <p data-testid={`customer-slip-utr-error-${idx}`} className="mt-1 text-[11px] font-medium text-rose-600">{UTR_UPLOAD_FORMAT_ERROR}</p>
                            ) : (item.uploadError && /utr/i.test(item.uploadError) && (
                                <p data-testid={`customer-slip-utr-error-${idx}`} className="mt-1 text-[11px] font-medium text-rose-600">{item.uploadError}</p>
                            ))}
                        </div>
                        <div>
                            <Label className="text-[10px] uppercase text-slate-500">App</Label>
                            <Select value={item.payment_app || "PhonePe"} onValueChange={(v) => onChange({ payment_app: v })} disabled>
                                <SelectTrigger data-testid={`customer-slip-app-${idx}`} className="mt-0.5 h-9 text-sm cursor-not-allowed bg-slate-100 text-slate-600" title="Auto-extracted from your slip — cannot be edited"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="PhonePe">PhonePe</SelectItem>
                                    <SelectItem value="Google Pay">Google Pay</SelectItem>
                                    <SelectItem value="Paytm">Paytm</SelectItem>
                                    <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                                    <SelectItem value="Other">Other</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="col-span-2">
                            <Label className="text-[10px] uppercase text-slate-500">Note (optional)</Label>
                            <Textarea
                                data-testid={`customer-slip-note-${idx}`}
                                rows={1}
                                value={item.notes}
                                onChange={(e) => onChange({ notes: e.target.value })}
                                className="mt-0.5 text-sm"
                                placeholder="Anything the admin should know about this slip"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
